// Hideo - immediate online saving.
// Every change is already written to the browser right away (persist, src/features/storage/save.ts), which calls
// queueCloudSync(). From here:
//  1. the profile goes online ~2 s after the last change (at most every 15 s while the user keeps typing);
//  2. right away when the user leaves the page or switches to another app (visibilitychange / pagehide);
//  3. without internet or after an error it waits and retries by itself (5 s, 15 s, 30 s, then every minute; at once when back online);
//  4. when the profile opens, when the user comes back to the app and every 2 minutes it checks whether a newer version saved
//     by another device is online and loads it by itself; if there are also unsaved changes here it asks which one to keep, and
//     the other one stays online as a copy ("… (copia del …)"), so nothing is lost;
//  5. #syncBtn indicator at the top right (drawn by TopControls.vue) (Saved / Saving… / To save / Offline / Tap to reactivate);
//  6. the Google sign-in survives a page reload until it expires (google.ts, key creatorOS_gtoken);
//  7. the Google sign-in (~1 hour) can only be renewed with a user gesture: when it is about to expire it is renewed at the first tap.
// Applies to profiles linked to a service (saved online or loaded from there at least once from this device).
// Point 7 is a stopgap: with a small renewal server (Cloudflare Worker, to do) the sign-in would never expire.
// The state is AS in src/stores/sync.ts. Ported from the legacy cloud/autosync.js (verbatim copy in
// tests/fixtures/legacy/autosync.js, golden tests in tests/saving*.test.ts): same timings, same rules.
import { LOCALE, t, tv } from '../../i18n'
import { useDataStore } from '../../stores/data'
import { useSyncStore } from '../../stores/sync'
import { useUiStore, type SyncView } from '../../stores/ui'
import { useDialogsStore } from '../../stores/dialogs'
import { useSettingsStore } from '../../stores/settings'
import { useCloudStore } from '../../stores/cloud'
import { useScriptsStore } from '../../stores/scripts'
import { useEditorStore } from '../../stores/editor'
import { syncToast, uiConfirm } from '../../stores/notices'
import { CS, cloudOn, driveOnline, saveDriveState } from './common'
import { CLOUDS, CLOUD_ORDER, anyCloud } from './services'
import { googleTokenNow, loadGis } from './google'
import { driveSave, linkLoaded, setAutoPaused } from './drive'
import { mediaPrefetch, mediaRetry } from './media'
import { validProfileFile, applyLoadedFile } from '../storage/files'
import { profileFileText } from '../storage/save'
import { fileStamp, fmtWhen, syncOf, timeParts } from '../storage/sync'
import { app } from '../app'

const SYNC_DELAY = 2000, SYNC_MAX_WAIT = 15000, SYNC_CHECK_EVERY = 120000, SYNC_RETRY = [5000, 15000, 30000, 60000]
const RENEW_BEFORE = 10 * 60000   // the Google sign-in is renewed at the first tap when less than 10 minutes are left
const AS = () => useSyncStore().AS
const PROFILES = () => useDataStore().profiles
/** Online autosave is on (and the page is served from a website) */
export const autoSyncOn = (): boolean => driveOnline && !!useSettingsStore().values.autoSaveMin
/** Services a profile is linked to on this device */
export const syncTargets = (id: string): string[] => CLOUD_ORDER.filter(pid => cloudOn(pid) && CS(pid).links[id])
const syncPendingIds = () => PROFILES().list.map(p => p.id).filter(id => syncOf(id).dirty && syncTargets(id).length)
export const hhmm = (time: number | string): string => new Date(time).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })
// Dialogs where the user is typing: data is not replaced with the online version while they are open (we wait until they
// close) (the Vue dialogs say it themselves: AppDialog "editing", e.g. the video, hook, creator and analysed video dialogs,
// the script editor and the teleprompter)
const syncEditing = () => useDialogsStore().editing()

// ===== 1. After every change =====
/** Saves online soon, after a change */
export function queueCloudSync(): void {
  if (!autoSyncOn()) return renderSyncUi()
  const as = AS(), now = Date.now(); if (!as.timer) as.first = now
  clearTimeout(as.timer as ReturnType<typeof setTimeout>)
  as.timer = setTimeout(cloudSyncNow, Math.max(0, Math.min(SYNC_DELAY, as.first + SYNC_MAX_WAIT - now)))
  renderSyncUi()
}
/** Saves online every profile with changes (usually only the active one) */
export async function cloudSyncNow(): Promise<void> {
  const as = AS()
  clearTimeout(as.timer as ReturnType<typeof setTimeout>); as.timer = null; clearTimeout(as.retryTimer as ReturnType<typeof setTimeout>); as.retryTimer = null
  if (!autoSyncOn()) return renderSyncUi()
  if (as.running || as.loading || as.conflict) { as.again = true; return }
  if (useSyncStore().busy) { as.timer = setTimeout(cloudSyncNow, 1500); return }   // a manual "Save / Load online" is running
  const ids = syncPendingIds()
  if (!ids.length) { if (as.state !== 'auth') as.state = as.at ? 'ok' : ''; return renderSyncUi() }
  if (navigator.onLine === false) { as.state = 'offline'; return renderSyncUi() }   // restarts with the "online" event
  as.running = true; as.again = false; renderSyncUi()
  let fail = '', conflict: { id: string; pid: string } | null = null
  const expired = new Set<string>()   // the services whose sign-in has expired
  try {
    for (const id of ids) for (const pid of syncTargets(id)) {
      const r = await driveSave(id, true, pid)
      if (r === 'conflict') conflict = conflict || { id, pid }
      else if (r !== 'ok' && fail !== 'auth') fail = r
      if (r === 'auth') expired.add(pid)
    }
  } finally { as.running = false }
  if (conflict) await syncConflict(conflict.id, conflict.pid)
  if (fail === 'auth') {
    // Each service with its own message and pause: Google comes back with a tap anywhere (state 'auth', armRenew), the
    // others from the Profiles window (before 24/10/2026 every expired sign-in was shown as Google's)
    expired.forEach(pid => {
      if (pid === 'google') { as.state = 'auth'; armRenew(); setAutoPaused(t('Your Google Drive sign-in has expired: tap anywhere in the app (or the save icon at the top) to reactivate it. Your changes are safe on this device.'), 'google') }
      else setAutoPaused(t('Your {service} sign-in has expired: connect it again in the Profiles window. Your changes are safe on this device.', { service: CLOUDS[pid].name }), pid)
    })
  }
  else if (fail === 'busy') as.timer = setTimeout(cloudSyncNow, 1500)
  else if (fail) syncRetry()
  else if (!conflict) as.retry = 0
  renderSyncUi()
  if (as.again || (!fail && syncPendingIds().length)) { as.again = false; queueCloudSync() }   // changes made during the save
}
// ===== 3. Retries =====
function syncRetry(): void {
  const as = AS()
  as.state = navigator.onLine === false ? 'offline' : 'error'
  if (as.state === 'offline') return   // restarts with the "online" event
  const wait = SYNC_RETRY[Math.min(as.retry, SYNC_RETRY.length - 1)]; as.retry++
  clearTimeout(as.retryTimer as ReturnType<typeof setTimeout>); as.retryTimer = setTimeout(cloudSyncNow, wait)
}

// ===== 4. Newer version saved by another device =====
/** Loads the newer version saved by another device (or asks, when there are changes here too) */
export async function cloudCheckRemote(force = false): Promise<void> {
  const as = AS()
  if (!autoSyncOn() || as.checking || as.running || as.loading || as.conflict || useSyncStore().busy || navigator.onLine === false) return
  const id = PROFILES().current; if (!id || !app.booted || useDialogsStore().isOpen('picker')) return
  const pid = syncTargets(id)[0]; if (!pid) return
  if (!force && Date.now() - as.lastCheck < 20000) return
  as.checking = true; as.lastCheck = Date.now()
  let next: (() => Promise<void>) | null = null
  try {
    if (!(await CLOUDS[pid].token(false))) { if (pid === 'google') { as.state = 'auth'; armRenew() } return }
    const S = CS(pid), remote = await CLOUDS[pid].meta(S.links[id])
    if (!remote || !remote.version || remote.version === S.seen[id]) return
    if (syncOf(id).dirty) next = () => syncConflict(id, pid)
    else if (syncEditing()) as.lastCheck = 0   // retried once the dialog is closed
    else next = () => syncLoadRemote(id, pid, remote)
  } catch (e) { console.warn(e) }
  finally { as.checking = false; renderSyncUi() }
  if (next) await next()
}
async function syncLoadRemote(id: string, pid: string, remote: { id: string; version?: string; savedOn?: string; savedAt?: string; modified?: string }): Promise<void> {
  const P = CLOUDS[pid], as = AS()
  as.loading = true; renderSyncUi()
  let conflict = false
  try {
    const d = JSON.parse(await P.readText(remote.id))
    if (!validProfileFile(d)) throw new Error('Invalid online file')
    if (syncOf(id).dirty) conflict = true   // changed while downloading
    else if (await applyLoadedFile(id, d, 'online-caricato', '', true)) {
      linkLoaded(pid, id, remote.id, remote.version)
      as.state = 'ok'; as.at = Date.now()
      syncToast(remote.savedOn
        ? t('Updated with the version saved from the {device} at {time}', { device: tv(remote.savedOn), time: hhmm((remote.savedAt || remote.modified) as string) })
        : t('Updated with the version saved online at {time}', { time: hhmm((remote.savedAt || remote.modified) as string) }))
      mediaPrefetch(d).then(mediaRetry).catch(e => console.warn(e))
    }
  } catch (e) { console.warn(e) }
  finally { as.loading = false; renderSyncUi() }
  if (conflict) await syncConflict(id, pid)
}
// Changed both here and on another device: the user picks which one to keep, the other version stays online as a copy
async function syncConflict(id: string, pid: string): Promise<void> {
  const as = AS()
  if (as.conflict) return; as.conflict = true; renderSyncUi()
  const P = CLOUDS[pid], S = CS(pid), p = PROFILES().list.find(x => x.id === id)
  let keepHere = false
  try {
    const remote = p && await P.meta(S.links[id]); if (!remote) return
    const prof = p as NonNullable<typeof p>
    // uiConfirm escapes the message: params stay plain text. The copy name is stored data, so it stays as it is ("copia del")
    const params = { name: prof.name, service: P.name, when: fmtWhen(remote.savedAt || remote.modified), device: tv(remote.savedOn) as string, copy: prof.name + ' (copia del …)' }
    keepHere = await uiConfirm(remote.savedOn
      ? t('"{name}" was changed both here and on another device.\n\nOn {service}: version of {when} from the {device}.\nHere: changes not saved online yet.\n\nWhich version do you want to keep? Nothing is lost: the other one stays on {service} as the copy "{copy}", which you can open with "Load online".', params)
      : t('"{name}" was changed both here and on another device.\n\nOn {service}: version of {when}.\nHere: changes not saved online yet.\n\nWhich version do you want to keep? Nothing is lost: the other one stays on {service} as the copy "{copy}", which you can open with "Load online".', params),
      { title: t('Two different versions'), ok: t('Keep this device'), cancel: t('Use {service}', { service: P.name }), icon: 'fa-code-compare' })
    if (keepHere) {
      await syncKeepCopy(pid, prof, await P.readText(remote.id))
      S.seen[id] = remote.version as string; saveDriveState()   // the following save overwrites the online version
    } else {
      await syncKeepCopy(pid, prof, await profileFileText(id, fileStamp()))
      const d = JSON.parse(await P.readText(remote.id))
      if (!validProfileFile(d)) throw new Error('Invalid online file')
      if (await applyLoadedFile(id, d, 'online-caricato', '', true)) {
        linkLoaded(pid, id, remote.id, remote.version)
        as.state = 'ok'; as.at = Date.now()
        mediaPrefetch(d).then(mediaRetry).catch(e => console.warn(e))
      }
    }
  } catch (e) { console.error(e); syncRetry(); keepHere = false }
  finally { as.conflict = false; renderSyncUi() }
  if (keepHere) void cloudSyncNow()
}
// Online safety copy: without the profile marker, so it is never mistaken for the profile file.
// The file name and the 'copia' marker are stored data: never translated
async function syncKeepCopy(pid: string, p: { name: string }, text: string): Promise<void> {
  const n = timeParts()
  const name = `Hideo - ${p.name} (copia del ${n.d}-${n.mo}-${n.y} ${n.h}.${n.mi}).json`
  await CLOUDS[pid].saveProfile(null, name, text, { ...fileStamp(), profileId: 'copia' })
}

// ===== 7. Renew the Google sign-in at the first tap =====
let renewArmed = false
const needRenew = () => {
  const gToken = googleTokenNow()
  return driveOnline && cloudOn('google') && PROFILES().list.some(p => CS('google').links[p.id])
    && !(gToken && gToken.exp - Date.now() > RENEW_BEFORE)
}
function armRenew(): void {
  if (renewArmed || !needRenew()) return
  renewArmed = true; loadGis().catch(() => {})   // Google library ready before the tap (the window must open immediately)
  document.addEventListener('pointerdown', renewOnGesture, true)
}
function disarmRenew(): void { renewArmed = false; document.removeEventListener('pointerdown', renewOnGesture, true) }
function renewOnGesture(e: Event): void {
  if (!needRenew()) return disarmRenew()
  const google = (window as unknown as { google?: { accounts?: unknown } }).google
  if (!google || !google.accounts || Date.now() - AS().lastRenew < 30000) return   // library not ready yet: next tap
  const target = e.target as Element | null
  if (target && target.closest && target.closest('#syncBtn')) return   // the button handles it
  disarmRenew(); void syncRenew()
}
async function syncRenew(): Promise<void> {
  const as = AS()
  as.lastRenew = Date.now()
  try {
    await CLOUDS.google.token(true)
    setAutoPaused('', 'google'); if (as.state === 'auth') as.state = as.at ? 'ok' : ''
    renderSyncUi(); mediaRetry()
    await cloudCheckRemote(true); void cloudSyncNow()
  } catch (err) {
    console.warn(err); as.state = 'auth'; renderSyncUi()
    setTimeout(armRenew, 30000)   // window closed or blocked: retry at a later tap
  }
}

// ===== 5. Indicator at the top right =====
/** What the indicator shows now (null = nothing: opened as a file, no profile) */
export function syncView(): SyncView | null {
  const as = AS(), id = PROFILES().current
  if (!driveOnline || !id || !useDataStore().currentProfile()) return null
  if (!anyCloud() || !syncTargets(id).length) return { icon: 'fa-cloud', cls: 'text-gray-500', txt: t('Only here'), tip: t('This profile is only saved on this device: tap to save it online (Google Drive). From then on it will save by itself.') }
  if (!useSettingsStore().values.autoSaveMin) return { icon: 'fa-cloud', cls: 'text-gray-500', txt: t('Autosave off'), tip: t('Online autosave is off (Settings › Saves).') }
  if (as.conflict) return { icon: 'fa-code-compare', cls: 'text-brand', txt: t('Choose'), tip: t('Changes here and on another device: choose which version to keep.') }
  if (as.running) return { icon: 'fa-arrows-rotate fa-spin', cls: 'text-brand', txt: t('Saving…'), tip: t('Saving online…') }
  if (as.loading) return { icon: 'fa-arrows-rotate fa-spin', cls: 'text-brand', txt: t('Updating…'), tip: t('The version saved on another device is on its way…') }
  const pending = !!as.timer || syncPendingIds().length > 0
  if (as.state === 'auth') return { icon: 'fa-hand-pointer', cls: 'text-brand', txt: t('Tap to reactivate'), tip: t('Your Google Drive sign-in has expired: tap here to reactivate it. Your changes are safe on this device.') }
  if (as.state === 'offline' || (pending && navigator.onLine === false)) return { icon: 'fa-plane', cls: 'text-gray-400', txt: t('Offline'), tip: t("You're offline: your changes are safe here and go online as soon as the connection is back.") }
  if (as.state === 'error' && pending) return { icon: 'fa-triangle-exclamation', cls: 'text-brand', txt: t('Retrying…'), tip: t('Online save failed: retrying by itself. Tap to retry now.') }
  if (pending) return { icon: 'fa-cloud-arrow-up', cls: 'text-gray-400', txt: t('To save'), tip: t('Changes on their way to the online save…') }
  return { icon: 'fa-circle-check', cls: 'text-green-400', txt: t('Saved'), tip: as.at ? t('Everything saved online at {time}.', { time: hhmm(as.at) }) : t('Everything saved online.') }
}
/** Redraws the indicator: #syncBtn at the top right (TopControls.vue) and the dot on the phone's profile button (MobileBar.vue) */
export function renderSyncUi(): void {
  const v = syncView()
  useUiStore().sync = v ? { icon: v.icon, cls: v.cls, txt: v.txt, tip: v.tip } : null
}
/** Click on the indicator: save now, choose the service, reactivate Google… */
export function syncBtnClick(): void {
  const as = AS(), id = PROFILES().current, v = syncView(); if (!v) return
  if (!anyCloud() || !syncTargets(id).length) return useCloudStore().openPick('save', id)
  if (!useSettingsStore().values.autoSaveMin) return useSettingsStore().open('saves')
  if (as.state === 'auth') { disarmRenew(); void syncRenew(); return }   // the click is the gesture Google needs
  if (as.running || as.loading || as.conflict) return
  if (as.timer || syncPendingIds().length) { as.retry = 0; void cloudSyncNow(); return }
  syncToast(v.tip); void cloudCheckRemote(true)
}
/** After picking or switching profile (picker store): first check for a newer online version, then save what is missing */
export async function cloudAfterProfile(): Promise<void> {
  renderSyncUi()
  if (!autoSyncOn()) return
  await cloudCheckRemote(true)
  if (syncPendingIds().length && AS().state !== 'auth') void cloudSyncNow()
}

// ===== 2. Leaving the page or switching to another app =====
function syncFlush(): void {
  if (useScriptsStore().editingId && useDialogsStore().isOpen('editor')) useEditorStore().save()   // editor text not written yet
  const as = AS()
  if (autoSyncOn() && (as.timer || (syncPendingIds().length && as.state !== 'auth'))) void cloudSyncNow()
}
/** The listeners of the autosave (leaving the page, back online) and its periodic check: installed once, while the app
 *  loads (loadApp, src/features/boot.ts), like the legacy file did */
export function installAutosync(doc: Pick<Document, 'addEventListener' | 'visibilityState'> = document, win: Pick<Window, 'addEventListener'> = window): void {
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') return syncFlush()
    void cloudCheckRemote().then(() => { if (AS().state !== 'auth' && syncPendingIds().length) void cloudSyncNow() })
    if (needRenew()) armRenew()
  })
  win.addEventListener('pagehide', syncFlush)
  win.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
    syncFlush()
    const as = AS()
    if (autoSyncOn() && (as.timer || as.running)) { e.preventDefault(); e.returnValue = '' }   // computer: the browser's "Leave site?" prompt while a save is running
  })
  win.addEventListener('online', () => { const as = AS(); as.retry = 0; if (as.state === 'offline') as.state = ''; renderSyncUi(); void cloudCheckRemote(true).then(() => { if (syncPendingIds().length) void cloudSyncNow() }) })
  win.addEventListener('offline', renderSyncUi)
  // Periodic check: newer online version, changes left behind, Google sign-in about to expire
  setInterval(() => {
    const as = AS()
    if (!autoSyncOn() || doc.visibilityState !== 'visible') return
    if (needRenew()) armRenew()
    if (Date.now() - as.lastCheck > SYNC_CHECK_EVERY) void cloudCheckRemote()
    if (!as.timer && !as.retryTimer && !as.running && as.state !== 'auth' && as.state !== 'offline' && syncPendingIds().length) void cloudSyncNow()
  }, 30000)
}
